import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';

import { Address } from '@fantaz/shared/data-access/models';
import { MaterialModule } from '@fantaz/shared/ui/material';
import { formatDate } from '@fantaz/shared/utils/date';

@Component({
  selector: 'fz-verification-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MaterialModule],
  templateUrl: './verification-dialog.component.html',
  styleUrls: ['./verification-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VerificationDialogComponent implements OnInit, OnDestroy {
  selectedOption: 'edit-info' | 'register-credit-card' | 'upload-id-picture' =
    'register-credit-card';

  isEnableCreditCardOptions = signal(true);

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    public dialogRef: MatDialogRef<VerificationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    if (data.attempts) {
      if (this.isDisabledCreditCardOptions) {
        this.selectedOption = 'upload-id-picture';
      }

      if (this.isDisabledCreditCardOptions && this.isDisabledUploadOptions) {
        this.selectedOption = 'edit-info';
      }
    }
  }

  ngOnInit(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { m: 'verification' },
      replaceUrl: true,
      queryParamsHandling: 'merge',
    });

    // get query params and check if kyc it required, and allowed all options
    // or only docs
    const queryParams = this.route.snapshot.queryParams;

    if (queryParams['kyc'] && queryParams['kyc'] === 'document') {
      this.isEnableCreditCardOptions.set(false);
    }
  }

  ngOnDestroy(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { m: null },
      replaceUrl: true,
      queryParamsHandling: 'merge',
    });
  }

  get message(): string {
    if (!this.isEnableCreditCardOptions()) {
      return 'Our community is made of real players like you! Please complete your account verification by uploading your ID picture.';
    }

    return `Our community is made of real players like you! Ready to have fun and
        win prizes? Please complete your account verification by registering a
        Credit Card or uploading your ID picture.`;
  }

  get attempts() {
    return this.data?.attempts;
  }

  get isDisabledCreditCardOptions(): boolean {
    const attempts = this.attempts;
    if (attempts?.current?.CC >= 0 && attempts?.max.CC >= 0) {
      return attempts.current.CC >= attempts.max.CC;
    }
    return false;
  }

  get isDisabledEditInfoOptions(): boolean {
    const attempts = this.attempts;
    if (
      attempts?.current?.CUSTOMER_IDENTITY >= 0 &&
      attempts?.max.CUSTOMER_IDENTITY >= 0
    ) {
      return (
        attempts.current.CUSTOMER_IDENTITY >= attempts.max.CUSTOMER_IDENTITY
      );
    }
    return false;
  }

  get isDisabledUploadOptions(): boolean {
    const attempts = this.attempts;
    if (attempts?.current?.DOCUMENT >= 0 && attempts?.max.DOCUMENT >= 0) {
      return attempts.current.DOCUMENT >= attempts.max.DOCUMENT;
    }
    return false;
  }

  get userFullName() {
    return `${this.data?.user.firstName || ''} ${
      this.data?.user.lastName || ''
    }`;
  }

  get birthday() {
    return this.data?.user?.birthday ? `${this.data.user.birthday}` : '';
  }

  get address(): Address {
    return new Address(this.data?.user.legalAddress);
  }

  selectOption(option: string) {
    this.dialogRef.close({
      option,
    });
  }

  formatBirthday(date: string): string {
    if (date) {
      return formatDate(date);
    }
    return '';
  }

  handleSelection() {
    this.selectOption(this.selectedOption);
  }

  handleClose() {
    this.dialogRef.close({
      source: 'close-button',
    });
  }
}
